import { ReactNode, useMemo } from 'react';
import ArrowUpRight from 'apps/cdp-web-demo/app/icons/arrowUpRight.svg';
import { AccountCreationType } from 'apps/cdp-web-demo/app/types';
import { Pictogram } from '@cbhq/cds-web/illustrations/Pictogram';
import { SpotIcon } from '@cbhq/cds-web/illustrations/SpotIcon';
import { Text } from '@cbhq/cds-web/typography';

import { Collapsible } from '../Collapsible/Collapsible';

import styles from './UserActions.module.css';

type Props = {
  accountCreationType: AccountCreationType;
  userAddress?: string;
  exportWallet: () => void;
  linkProfile: () => void;
  sendTransaction: () => void;
  addMfa: () => void;
};

type BaseAction = {
  label: string;
  dsecription: string;
  icon: ReactNode;
};

type LinkAction = BaseAction & { href: string };

type ButtonAction = BaseAction & { onClick: () => void };

type Action = BaseAction & (LinkAction | ButtonAction);

function isExternalAction(action: Action): action is LinkAction {
  return 'href' in action && typeof action.href === 'string' && !!action.href;
}

function UserAction(props: Action) {
  const { label, dsecription, icon } = props;
  const { Wrapper, wrapperProps, isExternal } = isExternalAction(props)
    ? {
        Wrapper: 'a' as const,
        wrapperProps: {
          href: props.href,
          target: '_blank',
          rel: 'noopener noreferrer',
          'aria-label': label,
        },
        isExternal: true,
      }
    : { Wrapper: 'div' as const, wrapperProps: { onClick: props.onClick }, isExternal: false };
  return (
    <Wrapper className={`${styles.row} ${styles.interactive}`} {...wrapperProps}>
      <div className={styles['row-icon']}>{icon}</div>
      <div className={styles['row-inner']}>
        <div className={styles.content}>
          <Text as="h3" font="label1">
            {label}
          </Text>
          <Text as="p" font="label2" color="fgMuted">
            {dsecription}
            {isExternal ? ' ' : ''}
          </Text>
        </div>
        {isExternal ? (
          <ArrowUpRight role="img" aria-hidden="true" className={styles['row-icon-external']} />
        ) : (
          // eslint-disable-next-line react/jsx-handler-names
          <button type="button" className="sr-only" onClick={wrapperProps.onClick}>
            {label}
          </button>
        )}
      </div>
    </Wrapper>
  );
}

function UserActions({
  accountCreationType,
  addMfa,
  exportWallet,
  linkProfile,
  sendTransaction,
}: Props) {
  const token = accountCreationType === 'solana' ? 'SOL' : 'ETH';

  const userActions = useMemo(() => {
    return [
      {
        label: 'Link a profile',
        dsecription: 'Add other profiles to this wallet',
        icon: <Pictogram name="accountsNavigation" aria-hidden="true" />,
        onClick: linkProfile,
      },
      {
        label: 'Add multi-factor authentication',
        dsecription: 'Require verification for sensitive actions',
        icon: <SpotIcon name="2fa" aria-hidden="true" />,
        onClick: addMfa,
      },
    ] satisfies Action[];
  }, [linkProfile, addMfa]);

  const walletActions = useMemo(() => {
    return [
      {
        label: 'Send a test transaction',
        dsecription: `Send 0.000001 ${token} from your wallet to a test wallet`,
        icon: <Pictogram name="sellSendAnytime" aria-hidden="true" />,
        onClick: sendTransaction,
      },
      {
        label: 'Export keys',
        dsecription:
          accountCreationType === 'evm-smart'
            ? "Copy the smart account owner's private key"
            : 'Copy private key to export wallet',
        icon: <Pictogram name="key" aria-hidden="true" />,
        onClick: exportWallet,
      },
      {
        label: 'Deposit USDC',
        dsecription: 'Fund your wallet in minutes',
        icon: <Pictogram name="fiat" aria-hidden="true" />,
        href: 'https://docs.cdp.coinbase.com/wallets/using-wallets/onramp/cross-platform',
      },
    ] satisfies Action[];
  }, [accountCreationType, exportWallet, sendTransaction, token]);

  return (
    <div className={styles.container}>
      <Collapsible triggerLabel="User actions">
        <ul className={styles.list}>
          {userActions.map((action) => (
            <li key={action.label}>
              <UserAction {...action} />
            </li>
          ))}
        </ul>
      </Collapsible>
      <Collapsible triggerLabel="Wallet actions">
        <ul className={styles.list}>
          {walletActions.map((action) => (
            <li key={action.label}>
              <UserAction {...action} />
            </li>
          ))}
        </ul>
      </Collapsible>
    </div>
  );
}

export { UserActions };
